import { useMemo } from 'react';
import { useQuery } from 'react-query';
import dataAPI from '../../../Jouly/services/dataAPI';
import { Point } from '../../../Jouly/types/types';
import { GridElement } from '../types';

type MetricStatus = 'ok' | 'warning' | 'unknown';

export interface MeterRealtimeMetric {
  title: 'Tension' | 'Consommation' | 'Puissance';
  value: number | null;
  formattedValue: string;
  unit: string;
  status: MetricStatus;
  trend: number[];
}

export interface MeterRealtimeMetrics {
  isMeterSelected: boolean;
  hasDeviceName: boolean;
  deviceName: string | null;
  isLoading: boolean;
  isError: boolean;
  voltage: MeterRealtimeMetric;
  consumption: MeterRealtimeMetric;
  power: MeterRealtimeMetric;
}

const EMPTY_METRIC: MeterRealtimeMetric = {
  title: 'Tension',
  value: null,
  formattedValue: '--',
  unit: '',
  status: 'unknown',
  trend: [],
};

const toFiniteNumber = (value: unknown): number | null => {
  const numericValue = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numericValue)) return null;
  return numericValue;
};

const getPointValue = (point?: Point): number | null => {
  if (!point) return null;
  const directValue = toFiniteNumber(point.Value);
  if (directValue !== null) return directValue;
  return toFiniteNumber(point.Values?._value);
};

const buildTrend = (points: Point[] | undefined, maxPoints = 18): number[] => {
  if (!points || points.length === 0) return [];
  const values = points
    .map((point) => getPointValue(point))
    .filter((value): value is number => value !== null);
  if (values.length <= maxPoints) return values;
  return values.slice(values.length - maxPoints);
};

const getLastValue = (points: Point[] | undefined): number | null => {
  if (!points || points.length === 0) return null;
  for (let index = points.length - 1; index >= 0; index -= 1) {
    const value = getPointValue(points[index]);
    if (value !== null) return value;
  }
  return null;
};

const formatNumeric = (value: number | null, fractionDigits = 1): string => {
  if (value === null) return '--';
  return value.toFixed(fractionDigits);
};

const nowToIsoDate = () => new Date().toISOString().slice(0, 10);

const yesterdayToIsoDate = () => {
  const date = new Date();
  date.setDate(date.getDate() - 1);
  return date.toISOString().slice(0, 10);
};

const resolveMeterDeviceName = (element: GridElement | null): string | null => {
  if (!element || element.type !== 'meter') return null;
  const fromNumber = element.number?.trim();
  if (fromNumber) return fromNumber;
  const fromMtrNumber = String(element.properties?.mtr_number ?? '').trim();
  if (fromMtrNumber) return fromMtrNumber;
  return null;
};

export const useMeterRealtimeMetrics = (selectedElement: GridElement | null): MeterRealtimeMetrics => {
  const isMeterSelected = selectedElement?.type === 'meter';
  const deviceName = resolveMeterDeviceName(selectedElement);
  const hasDeviceName = Boolean(deviceName);
  const enabled = isMeterSelected && hasDeviceName;

  const filter = useMemo(() => {
    if (!deviceName) return undefined;
    return {
      device_name: deviceName,
      startDate: yesterdayToIsoDate(),
      stopDate: nowToIsoDate(),
      aggregateWindow: '15m' as const,
    };
  }, [deviceName]);

  const voltageHistoryQuery = useQuery({
    queryKey: ['plan-bt-v2-meter-metrics', 'voltage-history', deviceName],
    queryFn: () => dataAPI.getVoltageSingleCounter(filter),
    enabled: enabled && !!filter,
    refetchInterval: 30000,
    staleTime: 10000,
  });

  const voltageLastQuery = useQuery({
    queryKey: ['plan-bt-v2-meter-metrics', 'voltage-last', deviceName],
    queryFn: () => dataAPI.getLastVoltageSingleCounter(filter),
    enabled: enabled && !!filter,
    refetchInterval: 30000,
    staleTime: 10000,
  });

  const consumptionHistoryQuery = useQuery({
    queryKey: ['plan-bt-v2-meter-metrics', 'consumption-history', deviceName],
    queryFn: () => dataAPI.getConsumptionSingleCounter(filter),
    enabled: enabled && !!filter,
    refetchInterval: 30000,
    staleTime: 10000,
  });

  const consumptionLastQuery = useQuery({
    queryKey: ['plan-bt-v2-meter-metrics', 'consumption-last', deviceName],
    queryFn: () => dataAPI.getLastConsumptionSingleCounter(filter),
    enabled: enabled && !!filter,
    refetchInterval: 30000,
    staleTime: 10000,
  });

  const powerHistoryQuery = useQuery({
    queryKey: ['plan-bt-v2-meter-metrics', 'power-history', deviceName],
    queryFn: () => dataAPI.getActivePowerPointSingleCounter(filter),
    enabled: enabled && !!filter,
    refetchInterval: 30000,
    staleTime: 10000,
  });

  const voltageValue = getLastValue(voltageLastQuery.data) ?? getLastValue(voltageHistoryQuery.data);
  const consumptionValue = getLastValue(consumptionLastQuery.data) ?? getLastValue(consumptionHistoryQuery.data);
  const rawPowerValue = getLastValue(powerHistoryQuery.data);

  const powerNormalized = rawPowerValue === null
    ? { value: null, unit: 'kW' }
    : rawPowerValue >= 1000
      ? { value: rawPowerValue / 1000, unit: 'kW' }
      : { value: rawPowerValue, unit: 'W' };

  const voltage: MeterRealtimeMetric = {
    title: 'Tension',
    value: voltageValue,
    formattedValue: formatNumeric(voltageValue, 1),
    unit: 'V',
    status: voltageValue === null ? 'unknown' : (voltageValue >= 180 && voltageValue <= 260 ? 'ok' : 'warning'),
    trend: buildTrend(voltageHistoryQuery.data),
  };

  const consumption: MeterRealtimeMetric = {
    title: 'Consommation',
    value: consumptionValue,
    formattedValue: formatNumeric(consumptionValue, 2),
    unit: 'kWh',
    status: consumptionValue === null ? 'unknown' : 'ok',
    trend: buildTrend(consumptionHistoryQuery.data),
  };

  const power: MeterRealtimeMetric = {
    title: 'Puissance',
    value: powerNormalized.value,
    formattedValue: formatNumeric(powerNormalized.value, 2),
    unit: powerNormalized.unit,
    status: powerNormalized.value === null ? 'unknown' : 'ok',
    trend: buildTrend(powerHistoryQuery.data).map((value) => (powerNormalized.unit === 'kW' ? value / 1000 : value)),
  };

  const isLoading = Boolean(
    enabled && (
      voltageHistoryQuery.isLoading ||
      voltageLastQuery.isLoading ||
      consumptionHistoryQuery.isLoading ||
      consumptionLastQuery.isLoading ||
      powerHistoryQuery.isLoading
    )
  );

  const isError = Boolean(
    enabled && (
      voltageHistoryQuery.isError ||
      voltageLastQuery.isError ||
      consumptionHistoryQuery.isError ||
      consumptionLastQuery.isError ||
      powerHistoryQuery.isError
    )
  );

  if (!isMeterSelected) {
    return {
      isMeterSelected: false,
      hasDeviceName: false,
      deviceName: null,
      isLoading: false,
      isError: false,
      voltage: { ...EMPTY_METRIC, title: 'Tension' },
      consumption: { ...EMPTY_METRIC, title: 'Consommation' },
      power: { ...EMPTY_METRIC, title: 'Puissance' },
    };
  }

  return {
    isMeterSelected: true,
    hasDeviceName,
    deviceName,
    isLoading,
    isError,
    voltage,
    consumption,
    power,
  };
};
